import styled from '@emotion/styled';
import { weblog } from '@myrealtrip/logger';
import { logBrazeEvent } from '@myrealtrip/marketing-log';
import { helper } from '@myrealtrip/frontend-weblog';
import { useDetermineMobile } from '@myrealtrip/device-detect';
import { IcoStar } from '@myrealtrip/web-icons';
import { globalVariables } from '../../../designSystem/constants';
import { WeblogPageCategory, WeblogScreenName } from '../../../constants/weblog';
import { useLogData } from '../LogDataContext';
import { type RecentViewProduct } from '../hooks/apis/useGetRecentViewProducts';

const { LoggingImpression } = weblog;
const { sendClickLog } = helper;

interface Props {
  product: RecentViewProduct;
  rank: number;
}

function RecentProductCard({ product, rank }: Props) {
  const isMobile = useDetermineMobile();
  const logData = useLogData();
  const firstTag = product.tags[0];

  const baseLog = {
    itemId: product.gid,
    itemKind: 'offer',
    itemName: product.title,
    systemProvider: product.systemProvider,
    itemVerticalIndex: 0,
    itemHorizonIndex: rank - 1,
    productDiscountRate: product.discountPercent,
    productPrice: product.discountText,
    manualYn: product.manualRegistered === 'Y' ? 'Y' : 'N',
    newBadgeYn: 'N',
    columnName: '최근 본 상품',
    columnCode: 'recent_products',
    columnIndex: isMobile ? 1 : 2,
  };

  const handleClick = () => {
    try {
      logBrazeEvent('enter_offer_detail', {
        source: 'recent_view_product',
        keyword: product.title,
        rank,
      });
      sendClickLog({
        eventName: 'gsearch',
        pageCategory: logData?.pageCategory ?? WeblogPageCategory.COMMON,
        screenName: logData?.screenName ?? WeblogScreenName.COMMON,
        ...baseLog,
        ...logData?.params,
      });
    } catch (e) {
      // ignore
    }
    window.location.href = product.productUrl;
  };

  return (
    <LoggingImpression
      screenName={logData?.screenName ?? WeblogScreenName.COMMON}
      eventName="gsearch"
      pageCategory={logData?.pageCategory ?? WeblogPageCategory.COMMON}
      params={{ ...baseLog, ...logData?.params }}
      impressionOptions={{ key: `RecentProductCard_${product.productUrl}`, threshold: 0.5 }}
    >
      <CardWrapper>
        <ThumbnailWrapper>
          <CardClickArea onClick={handleClick}>
            <Thumbnail src={product.imageUrl} alt={product.title} />
            {firstTag && (
              <TagBadge
                bgColor={firstTag.style.backgroundColor}
                textColor={firstTag.style.textColor}
              >
                {firstTag.text}
              </TagBadge>
            )}
          </CardClickArea>
        </ThumbnailWrapper>
        <CardInfo onClick={handleClick}>
          <ProductTitle>{product.title}</ProductTitle>
          <PriceRow>
            {product.discountPercent && <DiscountRate>{product.discountPercent}</DiscountRate>}
            <Price>{product.discountText}</Price>
          </PriceRow>
          {product.reviewCount > 0 && (
            <ReviewRow>
              <IcoStar color={globalVariables.yellow_400} size={14} />
              <ReviewScore>{product.reviewStar}</ReviewScore>
              <ReviewCount>({product.reviewCount.toLocaleString()})</ReviewCount>
            </ReviewRow>
          )}
        </CardInfo>
      </CardWrapper>
    </LoggingImpression>
  );
}

export default RecentProductCard;

const CardWrapper = styled.div`
  width: 130px;
  cursor: pointer;

  @media (max-width: 768px) {
    width: 80px;
  }
`;

const ThumbnailWrapper = styled.div`
  position: relative;
  width: 130px;
  height: 130px;
  border-radius: 8px;
  overflow: hidden;

  @media (max-width: 768px) {
    width: 80px;
    height: 80px;
  }
`;

const CardClickArea = styled.div`
  width: 100%;
  height: 100%;
`;

const Thumbnail = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const TagBadge = styled.span<{ bgColor: string; textColor: string }>`
  position: absolute;
  top: 8px;
  left: 8px;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  line-height: 16px;
  background-color: ${({ bgColor }) => bgColor};
  color: ${({ textColor }) => textColor};

  @media (max-width: 768px) {
    display: none;
  }
`;

const CardInfo = styled.div`
  margin-top: 8px;
`;

const ProductTitle = styled.span`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  color: ${globalVariables.gray_800};

  @media (max-width: 768px) {
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
  }
`;

const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
  margin-top: 6px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const DiscountRate = styled.span`
  font-size: 15px;
  font-weight: 800;
  color: #f53d6b;
`;

const Price = styled.span`
  font-size: 15px;
  font-weight: 800;
  color: ${globalVariables.gray_900};
`;

const ReviewRow = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
  margin-top: 4px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const ReviewScore = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: ${globalVariables.gray_900};
`;

const ReviewCount = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: ${globalVariables.gray_900};
`;
